import { useNavigate, useParams, generatePath } from "react-router-dom";
import { parseUrl } from "query-string";
import { useTranslation } from "i18n";
import { useJwtSilentRefresh } from "shared-auth";
import { ExpiryTimeUnits, setCookie } from "shared-utils";
import {
  checkoutAccessTypeMap,
  CheckoutAccessTypes,
  CheckoutFlow,
  PlanCodePeriod,
  PlanSelectorSearchParams,
} from "component-library/src/components/Organisms/Billing/types";
import { BASE_PATH, CHECKOUT_COMPLETE_FULL_PATH } from "config/common";
import {
  BillingUser,
  BillingUserWithoutCommon,
} from "translations/src/models/billing";
import useGetUserType from "./useGetUserType";
import { AddOnCodes } from "component-library/src/components/Organisms/Billing/constants";

const PAYMENT_COOKIE = "PaymentStatus";

export const useGetBillingContextDefaultProps = (
  checkoutFlow: CheckoutAccessTypes
) => {
  const jwtSilentRefresh = useJwtSilentRefresh();
  const parsedUrl = parseUrl(window.location.search);
  const preselectedPlan =
    parsedUrl.query[PlanSelectorSearchParams.PreSelectedPlan];

  let preselectedAddOns =
    parsedUrl.query[PlanSelectorSearchParams.PreSelectedAddOns];

  if (preselectedAddOns && !Array.isArray(preselectedAddOns)) {
    preselectedAddOns = [preselectedAddOns];
  }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type } = useParams<{
    type: string;
  }>();

  const userType = useGetUserType();

  const handleNavigateToCompletePage = (isDirectDebit?: boolean) => {
    navigate(
      generatePath(CHECKOUT_COMPLETE_FULL_PATH, {
        type: type ?? "",
        userType: userType ?? "",
      }),
      {
        replace: true,
        state: { isDirectDebit },
      }
    );
  };

  const checkoutFlowType = checkoutFlow
    ? checkoutAccessTypeMap[checkoutFlow]
    : CheckoutFlow.Default;

  const successCallback = async () => {
    await jwtSilentRefresh();

    setCookie(PAYMENT_COOKIE, Date.now(), {
      expiryTime: 30,
      expiryTimeUnit: ExpiryTimeUnits.MIN,
    });
  };

  return {
    billingUserType: userType as BillingUserWithoutCommon,
    goBackText: t("common:button.label.back"),
    onPaymentSuccessGoBackAction: () => navigate(BASE_PATH),
    navigateToCompletePage: handleNavigateToCompletePage,
    checkoutFlow: checkoutFlowType,
    isCommonHeader: userType !== BillingUser.Performer,
    successCallback: successCallback,
    preselectedPlanOverride: preselectedPlan as PlanCodePeriod,
    preselectedAddOnsOverride: preselectedAddOns as AddOnCodes[],
  };
};
